import { parseISO, format } from "date-fns";

import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { useApi } from "../../../Api/useApi.ts";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../../ui/dialog.tsx";
import { Badge } from "../../ui/badge.tsx";
import { Label } from "../../ui/label.tsx";
import { Button } from "../../ui/button.tsx";
import { Separator } from "../../ui/separator.tsx";
import { ScrollArea } from "../../ui/scroll-area.tsx";

import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs.tsx";
import {
    PackageSearch,
    RefreshCcw,
    PackageCheck,
    LoaderCircle,
} from "lucide-react";

import clsx from "clsx";
import { StepsLayout } from "./IntegrationBody.tsx";
import { noWebhookDeliveriesIcon } from "./icons.js";

type WebhookDeliveriesProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    webhookId?: string;
    webhookUrl?: string;
};

export const WebhookDeliveries = ({
    open,
    onOpenChange,
    webhookId,
    webhookUrl,
}: WebhookDeliveriesProps) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const [selectedTab, setSelectedTab] = useState("request");
    const [selectedDelivery, setSelectedDelivery] = useState({});

    const {
        data: deliveriesData,
        error,
        isFetching,
    } = useQuery({
        queryKey: ["deliveries", webhookId],
        queryFn: () => api.integrations.getWebhookLogs(webhookId as string),
        staleTime: Infinity,
        enabled: !!webhookId, // Only run query if id exists
        retry: 1,
    });

    const handleTabChange = (value: string) => {
        setSelectedTab(value);
    };

    const handleRefetch = () => {
        if (webhookId) {
            api.integrations.getWebhookLogs(webhookId);
        }
    };

    useEffect(() => {
        if (deliveriesData) {
            setSelectedDelivery(deliveriesData[0]);
        }
    }, [deliveriesData]);

    function formatDateTime(input: string, type: "date" | "time"): string {
        const serverDate = parseISO(input);

        // Convert to local time by constructing new Date from ISO string
        const localDate = new Date(
            serverDate.getTime() + new Date().getTimezoneOffset() * 60000,
        );

        if (type === "date") {
            return format(localDate, "d MMMM yyyy");
        }

        if (type === "time") {
            return format(localDate, "hh:mm:ss a");
        }

        return "";
    }

    // Mutation for redelivering a webhook event

    const redeliverMutation = useMutation({
        mutationFn: (eventId: string) =>
            api.integrations.redeliverWebhook(eventId),
        onSuccess: () => {
            // Optionally, you can refetch the deliveries after a successful redelivery
            handleRefetch();
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        },
        onError: (error) => {
            console.error("Redelivery failed:", error);
        },
    });

    const handleRedeliver = (eventId: string) => {
        redeliverMutation.mutate(eventId);
    };

    // Mutation for sending a test request
    const sendTestRequest = useMutation({
        mutationFn: (webhookId: string) =>
            api.integrations.testWebhookRequest(webhookId),
        onSuccess: () => {
            // Optionally, you can refetch the deliveries after a successful redelivery
            handleRefetch();
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        },
        onError: (error) => {
            console.error("test webhook request failed:", error);
        },
    });

    const handleSendTestRequest = () => {
        sendTestRequest.mutate(webhookId as string);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:w-[840px] max-w-[840px] max-h-[600px] w-full p-0 gap-0 top-[50%]">
                <DialogHeader className="p-3 h-[61px] ">
                    <DialogTitle className="flex items-center gap-1">
                        <PackageSearch size={13} />
                        <span className=" text-[14px]"> Recent deliveries</span>
                    </DialogTitle>
                    <DialogDescription className=" text-[12px] max-w-[500px] truncate">
                        Recent deliveries that sent to - {webhookUrl}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center h-full border-t border-t-[#F1F2F4]">
                    {/* @ts-ignore */}
                    {error?.status === 404 || deliveriesData?.length <= 0 ? (
                        <StepsLayout
                            title="No deliveries found"
                            description="No deliveries have been sent to this webhook yet."
                            icon={noWebhookDeliveriesIcon}
                        >
                            <Button
                                variant="default"
                                className="flex items-center gap-2 text-xs font-normal rounded-md h-9 "
                                onClick={handleSendTestRequest}
                                disabled={sendTestRequest.isPending}
                            >
                                send test request
                            </Button>
                        </StepsLayout>
                    ) : isFetching ? (
                        <StepsLayout title="" description="" icon={""}>
                            <LoaderCircle
                                className="animate-spin text-primary"
                                size={24}
                            />
                        </StepsLayout>
                    ) : (
                        <>
                            <div className="flex w-full border-b border-b-[#F1F2F4] h-[478px]">
                                {/* Sidebar */}
                                <div className="w-[250px] flex flex-col gap-1 h-full border-r border-r-[#F1F2F4] ">
                                    <div className=" flex justify-between px-3 items-center border-b h-[61px] border-b-[#F1F2F4] ">
                                        <Label className="text-xs font-normal text-[#8590A2]">
                                            Delivery ID
                                        </Label>
                                        <Button
                                            variant={"outline"}
                                            className="flex items-center p-2.5 gap-2 text-xs h-9 font-normal "
                                            onClick={handleRefetch}
                                            disabled={isFetching}
                                        >
                                            <RefreshCcw
                                                className=" text-[#191B33]"
                                                size={12}
                                            />
                                            Refresh
                                        </Button>
                                    </div>

                                    {/* RequestCard */}
                                    <div className="">
                                        <ScrollArea className="h-[400px] px-1">
                                            {Array.isArray(deliveriesData) &&
                                                deliveriesData.length > 0 &&
                                                deliveriesData.map(
                                                    (delivery, index) => (
                                                        <div
                                                            key={index}
                                                            className={clsx(
                                                                "p-4 cursor-pointer my-1 rounded-tr-md rounded-md hover:bg-slate-50",
                                                                {
                                                                    "bg-slate-100":
                                                                        //@ts-ignore
                                                                        selectedDelivery._id ===
                                                                        delivery._id,
                                                                },
                                                            )}
                                                            onClick={() => {
                                                                setSelectedDelivery(
                                                                    delivery,
                                                                );
                                                            }}
                                                        >
                                                            <div className="flex items-center gap-3 mb-1">
                                                                <div
                                                                    className={clsx(
                                                                        "flex items-center justify-center w-5 h-5  rounded-full",
                                                                        delivery.status_code ===
                                                                            200
                                                                            ? "bg-green-100"
                                                                            : "bg-red-100",
                                                                    )}
                                                                >
                                                                    {delivery.status_code ===
                                                                    200 ? (
                                                                        <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                                                                    ) : (
                                                                        <div className="h-2.5 w-2.5 rounded-full bg-red-500"></div>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs font-medium">
                                                                    {formatDateTime(
                                                                        delivery.created_at,
                                                                        "time",
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {formatDateTime(
                                                                        delivery.created_at,
                                                                        "date",
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="ml-8 font-mono text-xs truncate text-muted-foreground">
                                                                {delivery._id}
                                                            </div>
                                                            {delivery.label && (
                                                                <div className="mt-2 ml-8 text-xs font-medium">
                                                                    {
                                                                        delivery.label
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                    ),
                                                )}
                                        </ScrollArea>
                                    </div>
                                </div>

                                {/* Main canvas  */}
                                {/* Header */}
                                <div className="w-full h-full ">
                                    <div className=" w-full flex justify-between px-3 items-center border-b h-[61px] border-b-[#F1F2F4] ">
                                        <Tabs
                                            defaultValue="request"
                                            className="w-[208px] h-9"
                                            onValueChange={handleTabChange}
                                            value={selectedTab}
                                        >
                                            <TabsList className="w-full h-9 px-0.5">
                                                <TabsTrigger
                                                    className="text-xs h-7 "
                                                    value="request"
                                                >
                                                    Request
                                                </TabsTrigger>
                                                <TabsTrigger
                                                    value="response"
                                                    className="flex items-center gap-1 text-xs h-7"
                                                >
                                                    Response
                                                    {/* @ts-ignore */}
                                                    {selectedDelivery?.status_code ===
                                                    200 ? (
                                                        <Badge
                                                            variant={"outline"}
                                                            className=" rounded-[4px] text-[#4CAF50] text-[10px] bg-[#4CAF501A] border-[#4caf4fab] text-xs text-center font-normal"
                                                        >
                                                            200
                                                        </Badge>
                                                    ) : (
                                                        <Badge
                                                            variant={"outline"}
                                                            className=" rounded-[4px] text-[#F44336] text-[10px] bg-[#F443361A] border-[#F443364D] text-xs text-center font-normal"
                                                        >
                                                            400
                                                        </Badge>
                                                    )}
                                                </TabsTrigger>
                                            </TabsList>
                                        </Tabs>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-2 text-xs font-normal h-9 "
                                            onClick={() =>
                                                handleRedeliver(
                                                    //@ts-ignore
                                                    selectedDelivery.event_id,
                                                )
                                            }
                                        >
                                            <PackageCheck size={16} />
                                            Redeliver
                                        </Button>
                                    </div>

                                    {/* Body */}
                                    <div className="flex flex-col h-full gap-2 px-3 py-2">
                                        <div className="flex flex-col w-full h-full gap-1">
                                            <Label className="text-xs font-normal text-[#8590A2]">
                                                Request
                                            </Label>
                                            <ScrollArea className="h-[380px] w-full rounded-[4px] ">
                                                {}
                                                <pre className="text-xs font-normal text-[#191B33] whitespace-pre-wrap p-3">
                                                    {selectedTab === "request"
                                                        ? JSON.stringify(
                                                              // @ts-ignore
                                                              selectedDelivery?.payload,
                                                              null,
                                                              2,
                                                          )
                                                        : JSON.stringify(
                                                              // @ts-ignore
                                                              selectedDelivery?.response_data,
                                                              null,
                                                              2,
                                                          )}
                                                </pre>
                                            </ScrollArea>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className=" flex w-full  items-center px-2 h-[61px]">
                                <div className="flex items-center hidden gap-2">
                                    <Label className="text-xs font-normal text-[#8590A2]">
                                        Requests Success Rate
                                    </Label>
                                    <Badge
                                        variant={"outline"}
                                        className=" rounded-[4px] text-[#4CAF50] bg-[#4CAF501A] border-[#4caf4fab] text-xs font-normal"
                                    >
                                        200
                                    </Badge>
                                    <Badge
                                        variant={"outline"}
                                        className=" rounded-[4px] text-primary bg-[#F7F8F9] border-[#F1F2F4] text-xs font-normal"
                                    >
                                        100%
                                    </Badge>

                                    <Separator className="h-4 w-[1px] bg-[#E4E4E7]" />
                                    <Badge
                                        variant={"outline"}
                                        className=" rounded-[4px] text-[#F44336] bg-[#F443361A] border-[#F443364D] text-xs font-normal"
                                    >
                                        400
                                    </Badge>
                                    <Badge
                                        variant={"outline"}
                                        className=" rounded-[4px] text-primary bg-[#F7F8F9] border-[#F1F2F4] text-xs font-normal"
                                    >
                                        0%
                                    </Badge>
                                </div>
                                <DialogFooter className="flex items-end justify-end w-full gap-3">
                                    <Button
                                        className="bg-primary px-2.5 text-white rounded-[8px] h-9  font-normal text-xs"
                                        onClick={handleSendTestRequest}
                                        disabled={sendTestRequest.isPending}
                                    >
                                        Send Test Request
                                    </Button>
                                </DialogFooter>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
